import { apiUrl } from "../api/endpoints";
import React, { useState, useEffect, useContext, useMemo, Fragment } from "react";
import axios from "axios";


import Spinner from "../Components/Spinner";
import Alert from "../Components/Alert";
import { getErrorMessage } from "../utils/errorUtils";

import { LanguageContext } from "../context/LanguageContext";
import { useAppTheme } from "../context/ThemeContext";
import devTranslations from "../languages/device.json";
import { getThemeColorValue, normalizeThemeMode } from "../utils/themeColors";

import { CheckCircle, ThumbUp, Cancel } from "@mui/icons-material";
import Handshake from "@mui/icons-material/Handshake";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { capitalize } from "@mui/material";

import "../Components/modal.css";
import "./DatastreamStatusDetails.css";

const StatusIndicator = ({ statusKey, displayName, t, colorOverride }) => {
  const key = String(statusKey ?? "").toLowerCase();

  let Icon = InfoOutlined;
  let color = "var(--primary)";

  if (key.includes("perfect") || key.includes("excellent") || key.includes("ممتاز")) {
    Icon = CheckCircle;
    color = "#4caf50";
  } else if (key.includes("very good") || (key.includes("good") && !key.includes("very")) || key.includes("جيد")) {
    Icon = ThumbUp;
    color = "#8bc34a";
  } else if (key.includes("acceptable") || key.includes("ok") || key.includes("مقبول")) {
    Icon = Handshake;
    color = "#ff9800";
  } else if (key.includes("bad") || key.includes("poor") || key.includes("سيء")) {
    Icon = Cancel;
    color = "#f44336";
  }

  const finalColor = (typeof colorOverride === "string" && colorOverride.trim()) ? colorOverride : color;

  return (
    <div className="ds-status">
      <Icon className="ds-status-icon" style={{ color: finalColor }} />
      <span className="ds-status-text" style={{ color: finalColor }}>
        {t?.[key] || displayName || capitalize(key) || "—"}
      </span>
    </div>
  );
};

export const DatastreamStatusDetails = ({ statusLogId, close }) => {
  const { lang } = useContext(LanguageContext);
  const { mode } = useAppTheme();
  const themeMode = normalizeThemeMode(mode);
  const t = devTranslations?.[lang]?.deviceStatusLog || {};
  const isRtl = ["ar", "ur"].includes(lang);

  const [datastreamLogs, setDatastreamLogs] = useState(null);
  const [loading, setLoading] = useState(true);

  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: isRtl ? "left" : "right",
  });

  const showAlert = (type, message) => {
    setAlertState({ open: true, type, message, position: isRtl ? "left" : "right" });
  };

  const groupedData = useMemo(() => {
    if (!datastreamLogs || !Array.isArray(datastreamLogs)) return [];

    const groups = {};

    datastreamLogs.forEach((log) => {
      const evaluationName = log?.evaluation || log?.status;
      if (!evaluationName) return;

      const statusKey = String(evaluationName).toLowerCase();
      const pinName = log?.pin_name || log?.pin || "—";
      const value = log?.value;

      const rawColor = log?.evaluation_color || log?.color || null;
      const rawColorLight =
        log?.evaluation_color_light || log?.colorLight || log?.color_light || null;
      const evaluationColor = getThemeColorValue(
        { color: rawColor, colorLight: rawColorLight },
        "color",
        themeMode,
        null
      );

      if (!groups[statusKey]) {
        groups[statusKey] = {
          statusKey,
          displayName: evaluationName,
          color: evaluationColor,
          datastreams: [],
        };
      } else if (!groups[statusKey].color && evaluationColor) {
        groups[statusKey].color = evaluationColor;
      }

      groups[statusKey].datastreams.push({ label: pinName, value });
    });

    const arr = Object.values(groups);

    arr.sort((a, b) => {
      const aNo = String(a.displayName).toLowerCase().includes("no evaluation");
      const bNo = String(b.displayName).toLowerCase().includes("no evaluation");
      if (aNo && !bNo) return 1;
      if (!aNo && bNo) return -1;
      return String(a.displayName).localeCompare(String(b.displayName));
    });

    return arr;
  }, [datastreamLogs, themeMode]);

  useEffect(() => {
    if (!statusLogId) {
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const res = await axios.get(apiUrl(`device/datastream-logs/${statusLogId}`));

        if (res?.data?.status === "OK" && Array.isArray(res?.data?.data)) {
          setDatastreamLogs(res.data.data);
        } else {
          throw new Error("Invalid data format received.");
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        showAlert("error", `Failed to load datastream logs: ${errorMessage}`);
        setDatastreamLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [statusLogId]);

  return (
    <Fragment>
      <div id="modal" onClick={close}>
        <Alert
          open={alertState.open}
          close={() => setAlertState({ ...alertState, open: false })}
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
        />

        <div
          id="popup"
          className={`dashboard-modal ds-details-modal ${isRtl ? "rtl" : ""}`}
          onClick={(e) => e.stopPropagation()}
          dir={isRtl ? "rtl" : "ltr"}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-text">
              <h2>{t.detailsTitle || "Data Evaluation Details"}</h2>
              <p>
                {t.viewModeText || "You are in a "}
                <strong>{t.viewMode || "View"}</strong>
                {t.viewModeText2 || " mode. This data is read-only."}
              </p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content">
            <div className="dashboard-content ds-details-content">
              <div className="modal-info ds-details-info-banner">
                <div className="ds-details-info-icon">i</div>
                <div className="ds-details-info-text">
                  <h4>{t.summaryTitle || "Summary"}</h4>
                  <p>{t.summaryDesc || "Datastreams are grouped by their evaluation result."}</p>
                </div>
              </div>

              {loading ? (
                <div className="ds-loading">
                  <Spinner />
                </div>
              ) : groupedData.length === 0 ? (
                <div className="ds-empty">
                  {t.noData || "—"}
                </div>
              ) : (
                <div className="ds-sections">
                  {groupedData.map((group) => (
                    <div className="ds-section" key={group.statusKey}>
                      <div className="ds-section-header">
                        <StatusIndicator
                          statusKey={group.statusKey}
                          displayName={group.displayName}
                          t={t}
                          colorOverride={group.color}
                        />
                        <span className="ds-count">{group.datastreams?.length ?? 0}</span>
                      </div>

                      <div className="ds-grid">
                        {group.datastreams.map((ds, idx) => (
                          <div className="ds-item" key={`${group.statusKey}-${idx}`}>
                            <span className="ds-item-label">{ds.label}</span>
                            <span className="ds-item-value">{ds.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer ds-details-footer">
            <button className="btn btn-cancel" onClick={close}>
              {devTranslations?.[lang]?.addDevice?.buttonCancel || "Close"}
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default DatastreamStatusDetails;
